import * as Sentry from "@sentry/react";

import type { UserRole } from "../api";

import { getServerNow } from "../utils/server-time";
import { location } from "./location";

/** Where the production deployment is served (see `.github/workflows/deploy.yaml`). */
const prodHostname = "clv.seerlinq.com";

const maxPending = 20;

/** Namespace for the `trackOncePerSession` guards, so `reset` can find them. */
const sessionKeyPrefix = "mp-session:";

const sessionDurationMs = 60 * 60 * 1000;

type Mixpanel = typeof import("mixpanel-browser").default;

type PendingEvent = [event: string, props?: Record<string, unknown>];

/**
 * Product analytics (Mixpanel). CLV shares a Mixpanel project with the patient
 * app, so every event carries `origin` to tell the two apart.
 *
 * Three things have to line up before anything is sent, so that no build other
 * than the production deployment can reach the project:
 *
 * - a token, which only `.env.production` carries. It is a write-only project
 *   token that ships in the bundle either way, so it is committed rather than
 *   injected as a CI secret — rotating it is a release, not a secrets change.
 * - the production hostname, because the build mode alone is not enough: the
 *   integration Playwright suite builds `--mode production` for snapshot
 *   parity, so that bundle carries a live token too.
 * - `environment === prod` from the API's `/info`, so a production bundle
 *   pointed at a non-prod backend stays silent as well.
 */
export class Analytics {
  /** The two build/deploy halves of the gate — fixed for the life of the page. */
  private readonly couldTrack: boolean;

  /** The lazily imported client, set once `init` has run on it. */
  private client: Mixpanel | null = null;
  private loading: Promise<Mixpanel | null> | null = null;

  /** Whether the client is initialised *and* identified, so events can go out. */
  private enabled = false;

  /**
   * Events tracked before the API said who and where we are. The screen a user
   * lands on renders as soon as `/users/me` resolves, which is one request
   * ahead of `init` — without this, its event would be dropped.
   */
  private pending: PendingEvent[] = [];

  constructor(
    private readonly token: string,
    hostname: string,
  ) {
    this.couldTrack = Boolean(token) && hostname === prodHostname;
  }

  async init(opts: {
    userUUID: string;
    isProdEnv: boolean;
    role: UserRole | null;
  }) {
    if (this.enabled || !this.couldTrack || !opts.isProdEnv) {
      // Nothing will ever be sent, so don't hold events for a flush that isn't
      // coming (a production bundle pointed at a non-prod API stays here).
      this.pending = [];
      return;
    }

    const mixpanel = await this.load();

    if (!mixpanel) {
      this.pending = [];
      return;
    }

    try {
      // A session that ends without `logout` — a closed tab, a crash — leaves
      // the last user's distinct id in localStorage, and `identify` would then
      // send it as `$anon_distinct_id`, tying the two together at the server.
      // Shared clinical workstations are exactly the case this guards.
      if (mixpanel.get_distinct_id() !== opts.userUUID) {
        mixpanel.reset();
        // Their session guards go too, or this user's first events look
        // already-sent and never fire.
        this.clearSessionGuards();
      }

      // Run on every login, not just the first: after `reset` the client is
      // loaded but anonymous again, and the next user at this machine must not
      // inherit the last one's identity.
      mixpanel.identify(opts.userUUID);
      // After `reset`, which clears super properties.
      mixpanel.register({ origin: "clv", role: opts.role });
    } catch (error) {
      this.reportFailure("Failed to identify with Mixpanel", error);
      this.pending = [];
      return;
    }

    this.enabled = true;

    const buffered = this.pending;
    this.pending = [];

    for (const call of buffered) {
      this.send(...call);
    }
  }

  track(event: string, props?: Record<string, unknown>) {
    if (this.enabled) {
      this.send(event, props);
      return;
    }

    // Off the production deployment nothing can enable tracking later, so there
    // is no flush to hold the event for.
    if (this.couldTrack && this.pending.length < maxPending) {
      this.pending.push([event, props]);
    }
  }

  /**
   * Track at most one event of this name per session (see `sessionDurationMs`).
   */
  trackOncePerSession(event: string, props?: Record<string, unknown>) {
    // Bail before touching storage, so non-production builds leave no keys
    // behind.
    if (!this.couldTrack) {
      return;
    }

    // Server-synced, so a skewed client clock can't hide a user's activity or
    // let one event repeat freely.
    const now = getServerNow().getTime();
    const key = `${sessionKeyPrefix}${event}`;

    try {
      const sentAt = Number(localStorage.getItem(key));

      // `Number(null)` is 0 and a corrupt value is NaN; both mean "no live
      // session", and both compare false here.
      if (sentAt && now - sentAt < sessionDurationMs) {
        return;
      }

      localStorage.setItem(key, String(now));
    } catch {
      // Storage blocked (private mode, quota). Send anyway: a duplicate event
      // costs a fraction of the quota, a missing one loses a user's session.
    }

    this.track(event, props);
  }

  /** Drop the identity so the next user on this machine starts fresh. */
  reset() {
    this.pending = [];
    // Clinical workstations are shared, so the next user to log in here starts
    // a session of their own rather than inheriting this user's sent events.
    this.clearSessionGuards();

    if (!this.enabled || !this.client) {
      return;
    }

    // Cleared before the call, and independently of whether `reset` throws:
    // this client is no longer identified either way, so the next `init` has to
    // identify again rather than take the already-enabled early return.
    this.enabled = false;

    try {
      this.client.reset();
    } catch (error) {
      console.error("Failed to reset Mixpanel", error);
    }
  }

  /**
   * Import and initialise the client once, however many logins the page sees.
   * Loading it on demand keeps the client (430 kB, 129 kB gzipped — the ESM
   * entry point inlines Mixpanel's rrweb session recorder, which we never turn
   * on) out of the initial bundle. Every build still emits the chunk, `--mode
   * test` included; the win is that nothing off production ever fetches it.
   */
  private load() {
    this.loading ??= import("mixpanel-browser")
      .then((module) => {
        module.default.init(this.token, {
          api_host: "https://api-eu.mixpanel.com",
          // Send nothing we didn't write by hand.
          autocapture: false,
          track_pageview: false,
          // CLV URLs carry patient identifiers, so keep them out of every event.
          property_blacklist: [
            "$current_url",
            "$referrer",
            "$referring_domain",
            "$initial_referrer",
            "$initial_referring_domain",
          ],
          // No IP-derived geolocation, and the distinct id stays out of cookies
          // sent to our API.
          ip: false,
          persistence: "localStorage",
        });

        this.client = module.default;
        return this.client;
      })
      .catch((error: unknown) => {
        this.reportFailure("Failed to init Mixpanel", error);
        return null;
      });

    return this.loading;
  }

  private clearSessionGuards() {
    try {
      for (const key of Object.keys(localStorage)) {
        if (key.startsWith(sessionKeyPrefix)) {
          localStorage.removeItem(key);
        }
      }
    } catch {
      // Storage blocked, so there is nothing stored to clear.
    }
  }

  private send(event: string, props?: Record<string, unknown>) {
    try {
      this.client?.track(event, props);
    } catch (error) {
      console.error("Failed to track event", event, error);
    }
  }

  /**
   * A client that won't start is a "should never happen" we want visibility
   * into, though never in the user's face. Sentry directly rather than
   * `reportError`: `src/app/errors.ts` imports the API connector, which imports
   * this module.
   */
  private reportFailure(message: string, error: unknown) {
    console.error(message, error);
    Sentry.captureException(error);
  }
}

export const analytics = new Analytics(
  import.meta.env.VITE_MIXPANEL_TOKEN,
  new URL(location.href).hostname,
);
